import { StickyCallButton } from "@/components/StickyCallButton";
import { lazy } from "react";
import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { CookieConsent, ConsentScripts } from "@/components/CookieConsent";
import { DeferUntilIdle } from "@/components/DeferUntilIdle";

const ChatBot = lazy(() => import("@/components/ChatBot"));

import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { httpEquiv: "Content-Language", content: "hu-HU" },
      { title: "LAKROSZ Autószerviz" },
      {
        name: "description",
        content:
          "Megbízható autószerelő és autószerviz Egerben. Hibafeltárás, motorfelújítás, fék- és futóműjavítás, gumiszerelés.",
      },
      { name: "author", content: "Lakrosz Autószerviz" },
      { name: "robots", content: "index, follow" },
      { name: "googlebot", content: "index, follow" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "LAKROSZ Autószerviz" },
      { property: "og:locale", content: "hu_HU" },
      { property: "og:title", content: "LAKROSZ Autószerviz" },
      { name: "twitter:title", content: "LAKROSZ Autószerviz" },
      {
        property: "og:description",
        content:
          "Megbízható autószerelő és autószerviz Egerben. Hibafeltárás, motorfelújítás, fék- és futóműjavítás, gumiszerelés.",
      },
      {
        name: "twitter:description",
        content:
          "Megbízható autószerelő és autószerviz Egerben. Hibafeltárás, motorfelújítás, fék- és futóműjavítás, gumiszerelés.",
      },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/18dOrlbZWLYlJzTsb3uGAZfMT7C2/social-images/social-1779961927263-lakrosz-autoszerviz-logo.webp",
      },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/18dOrlbZWLYlJzTsb3uGAZfMT7C2/social-images/social-1779961927263-lakrosz-autoszerviz-logo.webp",
      },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="hu-HU">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <>
      <Outlet />
      <StickyCallButton />
      <CookieConsent />
      <ConsentScripts />
      <DeferUntilIdle>
        <ChatBot />
      </DeferUntilIdle>
    </>
  );
}
